<template>
  <transition name="auth-fade">
    <div v-if="visible" class="back-confirm-overlay" @click.self="handleClose">
      <transition name="auth-zoom">
        <div class="back-confirm-modal">
          <div class="back-confirm-blur"></div>
          <div class="back-confirm-card message-card">
            <!-- 图标 -->
            <div class="message-icon" :class="iconClass">
              <svg v-if="type === 'error'" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
              </svg>
              <svg v-else-if="type === 'success'" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <path d="M8 12L11 15L16 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            
            <!-- 消息内容 -->
            <div class="message-content">
              <h3 class="message-title">{{ title }}</h3>
              <p class="message-text">{{ message }}</p>
            </div>
            
            <!-- 按钮 -->
            <div class="message-actions">
              <button 
                class="confirm-button" 
                :class="buttonClass"
                @click="handleClose"
              >
                {{ buttonText }}
              </button>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </transition>
</template>

<script setup>
import { computed, watch, onMounted, onUnmounted } from 'vue'

// Props
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'error', // 'error' or 'success'
    validator: (value) => ['error', 'success'].includes(value)
  },
  message: {
    type: String,
    default: ''
  },
  title: {
    type: String,
    default: ''
  },
  buttonText: {
    type: String,
    default: '确定'
  },
  autoClose: {
    type: Number,
    default: 0 // 0 表示不自动关闭，其他数字表示多少毫秒后自动关闭
  }
})

// Emits
const emit = defineEmits(['close'])

// 计算属性
const iconClass = computed(() => {
  return props.type === 'error' ? 'icon-error' : 'icon-success'
})

const buttonClass = computed(() => {
  return props.type === 'error' ? 'button-error' : 'button-success'
})

// 自动关闭定时器
let autoCloseTimer = null

// 关闭弹窗
const handleClose = () => {
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
  emit('close')
}

// 监听 ESC 关闭
const onKeydown = (e) => {
  if (e.key === 'Escape' && props.visible) {
    handleClose()
  }
}

// 监听 visible 变化
watch(() => props.visible, (newVal) => {
  if (newVal && props.autoClose > 0) {
    // 设置自动关闭
    autoCloseTimer = setTimeout(() => {
      handleClose()
    }, props.autoClose)
  } else if (!newVal && autoCloseTimer) {
    // 清除定时器
    clearTimeout(autoCloseTimer)
    autoCloseTimer = null
  }
})

onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown)
  if (autoCloseTimer) {
    clearTimeout(autoCloseTimer)
  }
})
</script>

<style scoped>
/* 复用 AuthModal 的毛玻璃遮罩与卡片视觉 */
.back-confirm-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  z-index: 15000;
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(10px);
}

.back-confirm-modal {
  position: relative;
  display: inline-block;
  width: auto;
  min-width: 280px;
  max-width: min(386px, 92vw);
  height: auto;
  padding: 0;
  border-radius: 35px;
  overflow: hidden;
  isolation: isolate;
  background-color: rgba(0,0,0,0.18);
}

.back-confirm-blur {
  position: absolute;
  inset: -40px;
  background: rgba(245, 245, 245, 0.6);
  background-blend-mode: normal, color-dodge;
  filter: blur(20px);
  backdrop-filter: blur(40px);
  border-radius: 34px;
  z-index: 0;
}

.back-confirm-card {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 35px;
  padding: 32px 24px 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

/* 消息卡片 */
.message-card {
  align-items: center;
  text-align: center;
  padding-top: 20px !important;
}

/* 图标样式 */
.message-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.message-icon svg {
  width: 40px;
  height: 40px;
}

.icon-error {
  background: rgba(255, 107, 107, 0.15);
  color: #ff6b6b;
}

.icon-success {
  background: rgba(16, 185, 129, 0.15);
  color: #10b981;
}

/* 消息内容 */
.message-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.message-title {
  font-size: 20px;
  font-weight: 700;
  color: #000;
  margin: 0;
  letter-spacing: -0.02em;
}

.message-text {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.65);
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
}

/* 按钮区域 */
.message-actions {
  display: flex;
  justify-content: center;
  width: 100%;
}

.confirm-button {
  width: 75%;
  padding: 12px 32px;
  font-weight: 600;
  font-size: 15px;
  border: none;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.button-error {
  background: #ff6b6b;
  color: #fff;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.button-error:hover {
  background: #ff5252;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 107, 107, 0.4);
}

.button-error:active {
  transform: translateY(0);
  background: #ff4444;
  box-shadow: 0 2px 8px rgba(255, 107, 107, 0.3);
}

.button-success {
  background: #10b981;
  color: #fff;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.button-success:hover {
  background: #0ea572;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
}

.button-success:active {
  transform: translateY(0);
  background: #0d9668;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

/* 弹层淡入淡出 */
.auth-fade-enter-active,
.auth-fade-leave-active {
  transition: opacity 0.18s ease;
}

.auth-fade-enter-from,
.auth-fade-leave-to {
  opacity: 0;
}

/* 卡片轻微缩放与上浮 */
.auth-zoom-enter-active,
.auth-zoom-leave-active {
  transition: transform 0.18s ease, opacity 0.18s ease;
  will-change: transform, opacity;
}

.auth-zoom-enter-from,
.auth-zoom-leave-to {
  transform: translateY(6px) scale(0.98);
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 640px) {
  .message-icon {
    width: 56px;
    height: 56px;
  }
  
  .message-icon svg {
    width: 36px;
    height: 36px;
  }
  
  .message-title {
    font-size: 18px;
  }
  
  .message-text {
    font-size: 14px;
  }
  
  .back-confirm-card {
    padding: 28px 20px 20px;
    gap: 16px;
  }
}
</style>
